import { Component, inject, OnInit } from '@angular/core';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { ContractingAuthorityService } from '../contracting-authority.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AcDto, RefTypeEnum } from '@/types';
import { DynamicForm, SearchableSelectOption, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getContractingAuthorityFormObject } from '../utils/contracting-authority-form-object';
import { TabsModule } from 'primeng/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MembersComponent } from '../../members/members.component';
import { contractingAuthoritySchema } from '@/validations/contracting-authority.validation';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { forkJoin, BehaviorSubject } from 'rxjs';
import { SettingsSeuilsComponent } from '../../settings-seuils/settings-seuils.component';

@Component({
  selector: 'sm-update-contracting-authority',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    TranslateModule,
    TabsModule,
    MembersComponent,
    CommonModule,
    SettingsSeuilsComponent,
  ],
  templateUrl: './update-contracting-authority.component.html',
  styleUrl: './update-contracting-authority.component.scss',
})
export class UpdateContractingAuthorityComponent implements OnInit {
  refParamsService = inject(RefParamsService);
  contractingAuthorityRepository = inject(ContractingAuthorityRepository);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'settings.contractingAuthority.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.update',
      routerLink: ['/settings', 'contracting-authority', 'update'],
    },
  ];

  selectedTab = 'users';
  id = this.activatedRoute.snapshot.params['id'];
  logoUrl = '';

  formObject: DynamicForm = getFormBuilderObjectFactory();

  tuttelOptions: SearchableSelectOption[] = [];
  comOptions: SelectOption[] = [];
  paysOptions: SelectOption[] = [];
  commissionTypeOptions: { name: string; code: string }[] = [
    { name: 'TUTELLE', code: 'TUTELLE' },
    { name: 'INTERNE', code: 'INTERNE' },
    { name: 'AUTRE', code: 'AUTRE' },
  ];
  commissionOptions$ = new BehaviorSubject<{ name: string; code: number }[]>([]);

  ngOnInit() {
    this.initRouting();
    forkJoin({
      tutels: this.contractingAuthorityService.getTutels(),
      pays: this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY),
      data: this.contractingAuthorityService.getOneContractingAuthorityBatch(Number(this.id)),
    }).subscribe(({ tutels, pays, data }) => {
      this.tuttelOptions = tutels.map(ref => ({ name: ref.nom, code: ref.id, searchable: `${ref.nom}_${ref.abreviation}_${ref.nomAr}` }));
      this.paysOptions = pays.map((p: any) => ({ name: p.description, code: p.id }));

      // Set DTO and select values
      this.contractingAuthorityRepository.set('response', data);
      this.contractingAuthorityRepository.set('dto', {
        nom: data.nom,
        abreviation: data.abreviation ?? '',
        nomAr: data.nomAr ?? '',
        tutelId: data.tutel?.id,
        comId: data.com?.id !== undefined ? data.com.id : undefined,
        email: data.email ?? '',
        logo: data.logo ?? '',
        logoFile: undefined, // Will be set when user selects a file
        istTutel: data.istTutel ?? false,
        contact: data.contact ?? '',
        userIds: data.users?.map(user => user.id.toString()) || [],
        ancrage: data.ancrage ?? '',
        typeAffCommission: data.typeAffCommission ?? null,
        adresse: {
          id: data.adresse?.id,
          tel1: data.adresse?.tel1 ?? '',
          tel2: data.adresse?.tel2 ?? '',
          fax: data.adresse?.fax ?? '',
          adresse: data.adresse?.adresse ?? '',
          compAdresse: data.adresse?.compAdresse ?? '',
          codePostal: data.adresse?.codePostal ?? '',
          ville: data.adresse?.ville ?? '',
          paysId: data.adresse?.pays && typeof (data.adresse.pays as any).id === 'number' ? (data.adresse.pays as any).id : undefined,
        },
      });

      // Load logo URL if logo exists
      if (data.logo) {
        this.contractingAuthorityService.getAcPhoto(data.id).subscribe({
          next: imageData => {
            this.logoUrl = `data:image/${imageData.type};base64,${imageData.content}`;
            // Update the form object with the logo URL
            this.updateFormObject();
          },
          error: () => {
            this.logoUrl = '';
            this.updateFormObject();
          },
        });
      } else {
        this.updateFormObject();
      }
      this.contractingAuthorityRepository.set('tuttelSelected', data.tutel ? { name: data.tutel.nom, code: data.tutel.id } : undefined);
      this.contractingAuthorityRepository.set('comSelected', data.com ? { name: data.com.nom, code: data.com.id } : undefined);
      this.contractingAuthorityRepository.set(
        'paysSelected',
        data.adresse?.pays && typeof (data.adresse.pays as any).id === 'number'
          ? { name: data.adresse.pays.description, code: (data.adresse.pays as any).id }
          : undefined
      );

      // Handle commission selection from API response FIRST
      if (data.com) {
        this.contractingAuthorityRepository.setNested('dto', 'comId', data.com.id);
        this.contractingAuthorityRepository.set('comSelected', { name: data.com.nom, code: data.com.id });
      }

      // Handle commission type from API response
      if (data.typeAffCommission) {
        // Load commission options based on the type and then create form object
        this.loadCommissionOptionsAndCreateForm(data.typeAffCommission);
      } else {
        // No commission type, create form object immediately
        this.createFormObject();
      }
    });
  }

  getCommissionTypeId(typeCommission: string): string {
    switch (typeCommission) {
      case 'TUTELLE':
        return 'TUTELLE';
      case 'INTERNE':
        return 'INTERNE';
      case 'AUTRE':
        return 'AUTRE';
      default:
        return 'AUTRE';
    }
  }

  loadCommissionOptions(commissionTypeId: string) {
    let apiCall: any;
    const currentComId = this.contractingAuthorityRepository.getNested<number>('dto', 'comId');
    const currentComSelected = this.contractingAuthorityRepository.get('comSelected');

    switch (commissionTypeId) {
      case 'TUTELLE': // Commission Tutelle
        apiCall = this.contractingAuthorityService.getCommissionsByTutel(Number(this.id));
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      case 'INTERNE': // Commission Interne
        apiCall = this.contractingAuthorityService.getPrivateCommissions(Number(this.id));
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      case 'AUTRE': // Autre Commission
        apiCall = this.contractingAuthorityService.getAllCommissions(Number(this.id));
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject(); // Update form to get new commission options
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject(); // Update form to get new commission options
          },
        });
        break;
      default:
        this.commissionOptions$.next([]);
        this.updateFormObject(); // Update form to get new commission options
    }
  }

  updateFormObject() {
    this.formObject = getContractingAuthorityFormObject({
      store: this.contractingAuthorityRepository,
      tuttelOptions: this.tuttelOptions,
      comOptions: this.comOptions,
      paysOptions: this.paysOptions,
      isIstTutelDisabled: true,
      commissionTypeOptions: this.commissionTypeOptions,
      getCommissionOptions: () => this.commissionOptions$.value,
      onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
      isDetailsMode: false,
      contractingAuthorityService: this.contractingAuthorityService,
      logoUrl: this.logoUrl,
    });
  }

  createFormObject() {
    this.formObject = getContractingAuthorityFormObject({
      store: this.contractingAuthorityRepository,
      tuttelOptions: this.tuttelOptions,
      comOptions: this.comOptions,
      paysOptions: this.paysOptions,
      isIstTutelDisabled: true,
      commissionTypeOptions: this.commissionTypeOptions,
      getCommissionOptions: () => this.commissionOptions$.value,
      onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
      isDetailsMode: false,
      contractingAuthorityService: this.contractingAuthorityService,
      logoUrl: this.logoUrl,
    });
  }

  loadCommissionOptionsAndCreateForm(commissionTypeId: string) {
    let apiCall: any;
    const currentComId = this.contractingAuthorityRepository.getNested<number>('dto', 'comId');
    const currentComSelected = this.contractingAuthorityRepository.get('comSelected');

    switch (commissionTypeId) {
      case 'TUTELLE': // Commission Tutelle
        apiCall = this.contractingAuthorityService.getCommissionsByTutel(Number(this.id));
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      case 'INTERNE': // Commission Interne
        apiCall = this.contractingAuthorityService.getPrivateCommissions(Number(this.id));
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      case 'AUTRE': // Autre Commission
        apiCall = this.contractingAuthorityService.getAllCommissionsForAdd();
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      default:
        this.commissionOptions$.next([]);
        this.createFormObject();
    }
  }

  editContractingAuthority() {
    const id = this.contractingAuthorityRepository.getNested<number>('response', 'id');
    const contractingAuthority = this.contractingAuthorityRepository.get<AcDto>('dto');
    const result = contractingAuthoritySchema.safeParse(contractingAuthority);

    if (result.success) {
      const dto = this.contractingAuthorityRepository.get('dto') as any;
      const formData = new FormData();

      // Add all fields except adresse
      Object.keys(dto).forEach(key => {
        if (key !== 'adresse' && dto[key] !== undefined && dto[key] !== null) {
          if (dto[key] instanceof File) {
            formData.append(key, dto[key]);
          } else if (Array.isArray(dto[key])) {
            dto[key].forEach((item: any, index: number) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key, String(dto[key]));
          }
        }
      });

      // Add adresse as a single parameter
      if (dto.adresse) {
        formData.append('adresse', JSON.stringify(dto.adresse));
      }

      this.contractingAuthorityService.updateContractingAuthority(id, formData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.contractingAuthority.ac.message.success.edited'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.contractingAuthority.ac.message.error.edited'),
          });
        },
      });
    } else {
      this.contractingAuthorityRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  initRouting() {
    this.contractingAuthorityRepository.reset();
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment !== null && ['users', 'seuils'].includes(fragment)) {
        this.selectedTab = fragment;
      }
    });

    this.id = this.activatedRoute.snapshot.params['id'];
  }

  onTabChange(event: any) {
    this.selectedTab = event;
    this.router.navigate([], {
      fragment: event,
      replaceUrl: true,
    });
  }

  cancel() {
    this.router.navigate(['/settings', 'contracting-authority']);
  }
}
